import http from '../plugins/http'
import { handleApiError, validateHash } from './utils/errorHandler'

export async function verificarAtualizacoesReservas ({
  id_empresa,
  id_comissionado,
  permite_multiplicar_cota,
  ultimo_hash
}) {
  if (!id_empresa || typeof id_empresa !== 'number') {
    throw new Error('verificarAtualizacoesReservas: id_empresa é obrigatório e deve ser um número')
  }

  if (!id_comissionado || typeof id_comissionado !== 'number') {
    throw new Error('verificarAtualizacoesReservas: id_comissionado é obrigatório e deve ser um número')
  }

  if (typeof permite_multiplicar_cota !== 'boolean') {
    throw new Error('verificarAtualizacoesReservas: permite_multiplicar_cota é obrigatório e deve ser booleano')
  }

  if (ultimo_hash && (typeof ultimo_hash !== 'string' || ultimo_hash.length !== 32)) {
    throw new Error('verificarAtualizacoesReservas: ultimo_hash deve ser uma string de 32 caracteres (MD5)')
  }

  const params = {
    id_empresa,
    id_comissionado,
    permite_multiplicar_cota
  }

  if (ultimo_hash) {
    params.ultimo_hash = ultimo_hash
  }

  const requestId = `verificar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`

  try {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[Polling] Iniciando requisição ${requestId}`, { ultimo_hash })
    }

    // Faz a requisição com configurações otimizadas para polling
    // Timeout aumentado para 90s devido a:
    // 1. Sincronização com ERP pode ser lenta (procedure pesada)
    // 2. ⚡ SEM CACHE - polling sempre busca dados frescos do ERP
    // 3. Permite operação completa mesmo com banco lento
    const response = await http.get('/reservas/verificar-atualizacoes', {
      params,
      timeout: 90000, // 90 segundos (1.5 minutos)
      requestId, // Identificador único para esta requisição
      options: {
        silent: true, // Não exibe loading global
        unguarded: false // Requer autenticação
      }
    })

    if (process.env.NODE_ENV === 'development') {
      console.log(`[Polling] Requisição ${requestId} concluída`, {
        tem_atualizacao: response.data?.tem_atualizacao,
        quantidade: response.data?.quantidade_reservas
      })
    }

    // Valida estrutura da resposta
    if (!response.data) {
      throw new Error('verificarAtualizacoesReservas: Resposta da API está vazia')
    }

    const { tem_atualizacao, hash_atual, timestamp, quantidade_reservas } = response.data

    // Validações da resposta (defensivas)
    // Em produção, esses warnings são suprimidos para evitar poluição de logs
    if (process.env.NODE_ENV === 'development') {
      if (typeof tem_atualizacao !== 'boolean') {
        console.warn('verificarAtualizacoesReservas: tem_atualizacao não é booleano')
      }

      if (!hash_atual || typeof hash_atual !== 'string' || hash_atual.length !== 32) {
        console.warn('verificarAtualizacoesReservas: hash_atual inválido')
      }

      if (!timestamp) {
        console.warn('verificarAtualizacoesReservas: timestamp não fornecido')
      }

      if (typeof quantidade_reservas !== 'number') {
        console.warn('verificarAtualizacoesReservas: quantidade_reservas não é número')
      }
    }

    return response
  } catch (error) {
    // Usa errorHandler centralizado para enriquecer o erro
    throw handleApiError(error, 'verificarAtualizacoesReservas')
  }
}

export async function listarReservas ({
  id_empresa,
  id_comissionado,
  permite_multiplicar_cota,
  silent = false
}) {
  if (!id_empresa || typeof id_empresa !== 'number') {
    throw new Error('listarReservas: id_empresa é obrigatório e deve ser um número')
  }

  if (!id_comissionado || typeof id_comissionado !== 'number') {
    throw new Error('listarReservas: id_comissionado é obrigatório e deve ser um número')
  }

  if (typeof permite_multiplicar_cota !== 'boolean') {
    throw new Error('listarReservas: permite_multiplicar_cota é obrigatório e deve ser booleano')
  }

  try {
    const response = await http.get('/reservas/listar', {
      params: {
        id_empresa,
        id_comissionado,
        permite_multiplicar_cota
      },
      options: {
        silent
      }
    })

    return response
  } catch (error) {
    // Tratamento de erro similar ao verificarAtualizacoes
    if (error.response) {
      const status = error.response.status
      const data = error.response.data || {}

      if (status === 401) {
        error.message = 'Sessão expirada. Faça login novamente.'
        error.needsReauth = true
      } else if (status === 500) {
        error.message = data.message || 'Erro ao carregar lista de reservas'
      }
    } else if (error.code === 'ECONNABORTED') {
      error.message = 'Timeout ao carregar reservas'
      error.isTimeout = true
    } else if (!error.response) {
      error.message = 'Erro de conexão ao carregar reservas'
      error.isNetworkError = true
    }

    throw error
  }
}

export async function marcarNotificacoesEnviadas ({ ids }) {
  if (!ids || !Array.isArray(ids) || ids.length === 0) {
    throw new Error('marcarNotificacoesEnviadas: ids é obrigatório e deve ser um array não vazio')
  }

  if (!ids.every(id => typeof id === 'number')) {
    throw new Error('marcarNotificacoesEnviadas: todos os IDs devem ser números')
  }

  try {
    const response = await http.post('/reservas/marcar-notificacoes-enviadas', {
      ids
    }, {
      timeout: 5000,
      options: {
        silent: true
      }
    })

    return response
  } catch (error) {
    if (error.response) {
      const status = error.response.status
      const data = error.response.data || {}

      if (status === 400) {
        error.message = data.message || 'IDs de notificações inválidos'
      } else if (status === 500) {
        error.message = 'Erro ao marcar notificações como enviadas'
      }
    } else if (error.code === 'ECONNABORTED') {
      error.message = 'Timeout ao marcar notificações'
      error.isTimeout = true
    }

    throw error
  }
}

export function useReservasAPI () {
  return {
    verificarAtualizacoesReservas,
    listarReservas,
    marcarNotificacoesEnviadas
  }
}

export default {
  verificarAtualizacoesReservas,
  listarReservas,
  marcarNotificacoesEnviadas,
  useReservasAPI
}
